import * as React from 'react';
import { AppProvider } from '@toolpad/core/AppProvider';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import api from '../lib/api.js';

const signupSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

/**
 * @param {{open: boolean, onClose?: () => void, onSignedIn?: () => void}} props
 */
export default function Signin({ open, onClose, onSignedIn }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [signUpOpen, setSignUpOpen] = React.useState(false);
    const [signInValues, setSignInValues] = React.useState({ email: '', password: '' });
    const [signInError, setSignInError] = React.useState('');
    const [signingIn, setSigningIn] = React.useState(false);
    const [signingUp, setSigningUp] = React.useState(false);
    const [signUpError, setSignUpError] = React.useState('');
    const [signUpSuccess, setSignUpSuccess] = React.useState('');
    const [showSignInPassword, setShowSignInPassword] = React.useState(false);
    const [showSignUpPassword, setShowSignUpPassword] = React.useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(signupSchema),
    });

    const handleModalClose = () => {
        setSignUpOpen(false);
        reset();
        setSignInError('');
        setSignInValues({ email: '', password: '' });
        setShowSignInPassword(false);
        setShowSignUpPassword(false);
        onClose?.();
    };

    const handleSignUpOpen = () => {
        setSignUpError('');
        setSignUpSuccess('');
        setSignUpOpen(true);
    };
    const handleSignUpClose = () => {
        setSignUpOpen(false);
        reset();
        setSignUpError('');
        setSignUpSuccess('');
        setSigningUp(false);
        setShowSignUpPassword(false);
    };

    const handleFieldChange = (event) => {
        const { name, value } = event.target;
        setSignInValues((prev) => ({ ...prev, [name]: value }));
    };

    const extractMessage = (err, fallback) => {
        const data = err?.response?.data;
        if (typeof data === 'string') return data;
        if (typeof data?.error === 'string') return data.error;
        if (typeof data?.message === 'string') return data.message;
        if (typeof err?.message === 'string') return err.message;
        return fallback;
    };

    const handleSignIn = async (event) => {
        event.preventDefault();
        setSignInError('');

        if (!signInValues.email || !signInValues.password) {
            setSignInError('Email and password are required.');
            return;
        }

        try {
            setSigningIn(true);
            await api.post('/login', { ...signInValues });
            onSignedIn?.();
            handleModalClose();
        } catch (error) {
            setSignInError(extractMessage(error, 'Login failed'));
        } finally {
            setSigningIn(false);
        }
    };

    const handleSignUpSubmit = async (data) => {
        setSignUpError('');
        setSignUpSuccess('');
        try {
            setSigningUp(true);
            const response = await api.post('/signup', data);
            setSignUpSuccess(response.data?.message || 'Account created! You can sign in now.');
            reset();
        } catch (error) {
            setSignUpError(extractMessage(error, 'Signup failed'));
        } finally {
            setSigningUp(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleModalClose}
            fullScreen={fullScreen}
            maxWidth={false}
            PaperProps={{
                sx: {
                    background: 'transparent',
                    boxShadow: 'none',
                    m: 0,
                },
            }}
        >
            <AppProvider theme={theme}>
                <Box
                    sx={{
                        minHeight: fullScreen ? '100vh' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 2, sm: 4, md: 6 },
                        background: fullScreen ? 'linear-gradient(135deg, #020617, #0f172a)' : 'transparent',
                    }}
                >
                    <Paper
                        component="form"
                        onSubmit={handleSignIn}
                        elevation={8}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: 860,
                            p: { xs: 4, md: 6 },
                            borderRadius: 3,
                            minHeight: 460,
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'linear-gradient(145deg, #0b1120, #111c3a)',
                            color: '#e2e8f0',
                            border: '1px solid rgba(148, 163, 184, 0.25)',
                        }}
                    >
                        <IconButton
                            type="button"
                            aria-label="Close sign in"
                            onClick={handleModalClose}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                color: 'rgba(226,232,240,0.7)',
                                '&:hover': {
                                    color: '#f8fafc',
                                    backgroundColor: 'rgba(148, 163, 184, 0.12)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        <Stack spacing={4}>
                            <Box textAlign="center">
                                <Typography variant="h4" fontWeight={600} sx={{ mt: 1, color: '#fff' }}>
                                    Sign in
                                </Typography>
                                <Typography sx={{ color: 'rgba(226,232,240,0.7)', mt: 1 }}>
                                    Use your dashboard credentials to continue.   
                                </Typography>
                                <Divider sx={{ mt: 3, borderColor: 'rgba(148, 163, 184, 0.2)' }} />
                            </Box>

                            {signInError && (
                                <Alert
                                    severity="error"
                                    onClose={() => setSignInError('')}
                                    sx={{ backgroundColor: 'rgba(239,68,68,0.08)' }}
                                >
                                    {signInError}
                                </Alert>
                            )}

                            <Stack spacing={2}>
                                <TextField
                                    variant="outlined"
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={signInValues.email}
                                    onChange={handleFieldChange}
                                    fullWidth
                                    InputLabelProps={{ sx: { color: 'rgba(226,232,240,0.7)' } }}
                                    InputProps={{
                                        sx: {
                                            color: '#f8fafc',
                                            borderRadius: 2,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(148, 163, 184, 0.4)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    variant="outlined"
                                    label="Password"
                                    name="password"
                                    type={showSignInPassword ? 'text' : 'password'}
                                    value={signInValues.password}
                                    onChange={handleFieldChange}
                                    fullWidth
                                    InputLabelProps={{ sx: { color: 'rgba(226,232,240,0.7)' } }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="button"
                                                    aria-label={showSignInPassword ? 'Hide password' : 'Show password'}
                                                    onClick={() => setShowSignInPassword((prev) => !prev)}
                                                    edge="end"
                                                    sx={{ color: 'rgba(226,232,240,0.7)' }}
                                                >
                                                    {showSignInPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            color: '#f8fafc',
                                            borderRadius: 2,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(148, 163, 184, 0.4)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                        },
                                    }}
                                />
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        flex: 1,
                                        height: 56,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        boxShadow: '0 10px 25px rgba(56,189,248,0.35)',
                                        background: 'linear-gradient(120deg, #38bdf8, #0ea5e9)',
                                    }}
                                    disabled={signingIn}
                                >
                                    {signingIn ? 'Signing in…' : 'Sign in'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        flex: 1,
                                        height: 56,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderColor: 'rgba(148, 163, 184, 0.5)',
                                        color: '#e2e8f0',
                                        '&:hover': {
                                            borderColor: '#38bdf8',
                                        },
                                    }}
                                    onClick={handleSignUpOpen}
                                >
                                    Sign up
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>

                <Dialog
                    open={signUpOpen}
                    onClose={handleSignUpClose}
                    fullScreen={fullScreen}
                    maxWidth={false}
                    PaperProps={{
                        sx: {
                            background: 'transparent',
                            boxShadow: 'none',
                            m: 0,
                        },
                    }}
                >
                    <Box
                        sx={{
                            minHeight: fullScreen ? '100vh' : 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 2, sm: 4, md: 6 },
                            background: fullScreen ? 'linear-gradient(135deg, #020617, #0f172a)' : 'transparent',
                        }}
                    >
                        <Paper
                            component="form"
                            onSubmit={handleSubmit(handleSignUpSubmit)}
                            elevation={12}
                            sx={{
                                width: '100%',
                                maxWidth: 640,
                                p: { xs: 4, md: 5 },
                                borderRadius: 3,
                                background: 'linear-gradient(145deg, #0b1120, #111c3a)',
                                color: '#e2e8f0',
                                border: '1px solid rgba(148, 163, 184, 0.25)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                            }}
                        >
                            <Box textAlign="center">
                                <Typography variant="h4" fontWeight={600} sx={{ color: '#fff' }}>
                                    Create account
                                </Typography>
                                <Typography sx={{ color: 'rgba(226,232,240,0.7)', mt: 1 }}>
                                    Join the playground — it only takes a minute.
                                </Typography>
                                <Divider sx={{ mt: 3, borderColor: 'rgba(148, 163, 184, 0.2)' }} />
                            </Box>

                            {signUpError && (
                                <Alert
                                    severity="error"
                                    onClose={() => setSignUpError('')}
                                    sx={{ backgroundColor: 'rgba(239,68,68,0.08)' }}
                                >
                                    {signUpError}
                                </Alert>
                            )}

                            {signUpSuccess && (
                                <Alert
                                    severity="success"
                                    onClose={() => setSignUpSuccess('')}
                                    sx={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#dcfce7' }}
                                >
                                    {signUpSuccess}
                                </Alert>
                            )}

                            <Stack spacing={2}>
                                <TextField
                                    variant="outlined"
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    {...register('email', { required: true })}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                    InputLabelProps={{ sx: { color: 'rgba(226,232,240,0.7)' } }}
                                    InputProps={{
                                        sx: {
                                            color: '#f8fafc',
                                            borderRadius: 2,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(148, 163, 184, 0.4)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    variant="outlined"
                                    label="Password"
                                    type={showSignUpPassword ? 'text' : 'password'}
                                    fullWidth
                                    {...register('password')}
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    InputLabelProps={{ sx: { color: 'rgba(226,232,240,0.7)' } }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="button"
                                                    aria-label={showSignUpPassword ? 'Hide password' : 'Show password'}
                                                    onClick={() => setShowSignUpPassword((prev) => !prev)}
                                                    edge="end"
                                                    sx={{ color: 'rgba(226,232,240,0.7)' }}
                                                >
                                                    {showSignUpPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            color: '#f8fafc',
                                            borderRadius: 2,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(148, 163, 184, 0.4)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                            },
                                        },
                                    }}
                                />
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        flex: 1,
                                        height: 56,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderColor: 'rgba(148, 163, 184, 0.5)',
                                        color: '#e2e8f0',
                                        '&:hover': {
                                            borderColor: '#38bdf8',
                                        },
                                    }}
                                    onClick={handleSignUpClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        flex: 1,
                                        height: 56,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        boxShadow: '0 10px 25px rgba(34,197,94,0.35)',
                                        background: 'linear-gradient(120deg, #34d399, #059669)',
                                    }}
                                    disabled={signingUp}
                                >
                                    {signingUp ? 'Signing up…' : 'Sign up'}
                                </Button>
                            </Stack>
                        </Paper>
                    </Box>
                </Dialog>
            </AppProvider>
        </Dialog>
    );
}
